import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CurrencyInput from '../../CurrencyInput/components/CurrencyInput';
import { formatCalculatedNumber } from '../hooks/useV3OfferBudget';

const COUNT_BASIS = 'per_tickets_sold';

// A count basis sums available - comps - kills, so it is whole by construction.
const formatCount = (value) =>
  Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });

// The engine calculates on the unrounded basis, so a dollar-rounded preview
// disagrees with the row total it feeds. formatCalculatedNumber is what renders
// those totals — anything else puts two currency formats on one screen. (O-4962)
const formatPreviewValue = (calcType, value) =>
  (calcType === COUNT_BASIS ? formatCount(value) : formatCalculatedNumber(value));

const CalcTypeQuantityInput = ({
  calcType,
  calcTypeOptions,
  qtyValue,
  calculatedQtyValue,
  onCalcTypeChange,
  onQtyChange,
  onQtyBlur,
  onQtyFocus,
  disabled,
  baseValuePreviews,
  tabIndex
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const isFixed = calcType === 'fixed';
  const isQtyEditable = isFixed && !disabled;

  const updateDropdownPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom, left: rect.left, width: rect.width });
    }
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (
      dropdownRef.current && !dropdownRef.current.contains(e.target) &&
      containerRef.current && !containerRef.current.contains(e.target)
    ) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (dropdownOpen) {
      updateDropdownPos();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPos, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateDropdownPos, true);
      };
    }
  }, [dropdownOpen, handleClickOutside, updateDropdownPos]);

  const handleSelectOption = (value) => {
    onCalcTypeChange(value);
    setDropdownOpen(false);
  };

  const isCountBasis = calcType === COUNT_BASIS;
  const isMoneyBasis = !isFixed && !isCountBasis;
  const showsDerivedMoneyBasis = isMoneyBasis && calculatedQtyValue != null;

  // Rounding the derived basis to the dollar here would put back, one control
  // away, the discrepancy the preview just removed.
  const derivedQuantity = calculatedQtyValue == null
    ? (qtyValue || 0)
    : (isCountBasis ? formatCount(calculatedQtyValue) : Number(calculatedQtyValue).toFixed(2));

  const displayValue = isFixed
    ? (qtyValue === '' ? qtyValue : Number(qtyValue))
    : derivedQuantity;

  const renderDropdown = () => {
    if (!dropdownOpen || !dropdownPos) return null;

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: dropdownPos.top,
          left: dropdownPos.left,
          minWidth: Math.max(dropdownPos.width, 260),
          backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
          border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
          borderRadius: '6px',
          boxShadow: '0px 4px 12px rgba(10, 13, 18, 0.12)',
          zIndex: 9999,
          overflow: 'hidden',
          fontSize: '14px'
        }}
      >
        {calcTypeOptions.map((option) => {
          const isSelected = option.value === calcType;
          const preview = baseValuePreviews?.[option.value];

          return (
            <div
              key={option.value}
              data-testid={`calc-type-option-${option.value}`}
              onClick={() => handleSelectOption(option.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: isSelected ? 'var(--bk-c-2684ff, #2684FF)' : 'transparent',
                fontWeight: isSelected ? '500' : '400',
                color: isSelected ? 'var(--bk-c-ffffff, #ffffff)' : 'var(--bk-c-333333, #333333)'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--bk-c-f9fafb, #F9FAFB)';
              }}
              onMouseLeave={(e) => {
                if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span style={{ whiteSpace: 'nowrap' }}>{option.label}</span>
              {option.value !== 'fixed' && preview != null && preview !== 0 && (
                <span style={{ color: isSelected ? 'rgba(255,255,255,0.7)' : 'var(--bk-c-717680, #717680)', fontSize: '13px', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                  {formatPreviewValue(option.value, preview)}
                </span>
              )}
            </div>
          );
        })}
      </div>,
      document.body
    );
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        height: '100%',
        border: '1px solid var(--bk-c-d5d7da, #D5D7DA)',
        borderRadius: '0',
        boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
        backgroundColor: (disabled || !isFixed) ? 'var(--bk-c-fafafa, #FAFAFA)' : 'var(--bk-c-ffffff, #ffffff)',
        position: 'relative'
      }}
    >
      <CurrencyInput
        disabled={!isQtyEditable}
        style={{
          flex: 1,
          border: 'none',
          borderRadius: '0',
          boxShadow: 'none',
          height: '100%',
          textAlign: 'right',
          fontSize: '14px',
          padding: '8px 22px 8px 8px',
          backgroundColor: 'transparent',
          color: isQtyEditable ? 'var(--bk-c-333333, #333333)' : 'var(--bk-c-717680, #717680)',
          boxSizing: 'border-box',
          width: '100%',
          minWidth: 0
        }}
        maskOptions={{ prefix: showsDerivedMoneyBasis ? "$" : "", integerLimit: null, allowDecimal: isMoneyBasis, allowNegative: !isFixed }}
        placeholder="0"
        value={displayValue}
        onChange={(e) => {
          if (isQtyEditable) {
            onQtyChange(e);
          }
        }}
        onFocus={(e) => {
          if (isQtyEditable && onQtyFocus) onQtyFocus(e);
          else e.target.select();
        }}
        onBlur={(e) => { if (isQtyEditable && onQtyBlur) onQtyBlur(e); }}
        tabIndex={!isQtyEditable ? -1 : tabIndex}
      />
      {!disabled && (
        <div
          data-testid="calc-type-caret"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          <i
            className={`far fa-chevron-${dropdownOpen ? 'up' : 'down'}`}
            style={{ fontSize: '9px', color: 'var(--bk-c-717680, #717680)' }}
          />
        </div>
      )}
      {renderDropdown()}
    </div>
  );
};

CalcTypeQuantityInput.propTypes = {
  calcType: PropTypes.string.isRequired,
  calcTypeOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })).isRequired,
  qtyValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  calculatedQtyValue: PropTypes.number,
  onCalcTypeChange: PropTypes.func.isRequired,
  onQtyChange: PropTypes.func.isRequired,
  onQtyBlur: PropTypes.func,
  // Overrides the default select-on-focus. Only consulted while the qty is
  // editable (calc type "fixed"); derived quantities keep select-all.
  onQtyFocus: PropTypes.func,
  disabled: PropTypes.bool,
  baseValuePreviews: PropTypes.object,
  tabIndex: PropTypes.number
};

CalcTypeQuantityInput.defaultProps = {
  onQtyBlur: null,
  onQtyFocus: null,
  disabled: false,
  baseValuePreviews: {},
  tabIndex: -1
};

export default CalcTypeQuantityInput;
